import React, { useEffect, useRef, useState } from 'react';
import { TimeOptions } from 'src/types/date-time.types';

type TimeSelectorProps = {
  hours: string;
  minutes: string;
  period: string;
  className?: string;
  onTimeChange: (value: TimeOptions) => void;
};

const TimeSelector = ({
  hours,
  minutes,
  period,
  className = '',
  onTimeChange,
}: TimeSelectorProps) => {
  const [isUserScroll, setIsUserScroll] = useState(false);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const periodRef = useRef(null);
  const hourOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    i < 10 ? `0${i}` : i.toString()
  );
  const periodOptions = ['am', 'pm'];
  const scrollToSelected = (
    ref: React.RefObject<HTMLElement | null>,
    index: number,
    smooth = true
  ) => {
    if (ref.current) {
      ref.current.scrollTo({
        top: (index + 1) * 42,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  };
  const handleScroll = (
    ref: React.RefObject<HTMLElement | null>,
    setValue: React.Dispatch<React.SetStateAction<any>>,
    values: string[]
  ) => {
    if (ref.current) {
      const index = Math.round(ref.current.scrollTop / 42) - 1;
      if (values[index] !== undefined) {
        setValue(values[index]);
      }
    }
  };
  const setHour = (value: string) => {
    if (isUserScroll) {
      onTimeChange({ hours: value, minutes, period });
    }
  };
  const setMinute = (value: string) => {
    if (isUserScroll) {
      onTimeChange({ hours, minutes: value, period });
    }
  };
  const setPeriod = (value: string) => {
    if (isUserScroll) {
      onTimeChange({ hours, minutes, period: value });
    }
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollToSelected(hourRef, hourOptions.indexOf(hours), false);
      scrollToSelected(minuteRef, minuteOptions.indexOf(minutes), false);
      scrollToSelected(periodRef, periodOptions.indexOf(period), false);
    });

    setIsUserScroll(true);
  }, [hourRef, minuteRef, periodRef]);

  return (
    <div
      className={`flex items-center justify-center bg-grey rounded-[8px] text-[#110F09] p-4 font-inter ${className}`}
    >
      {/* Hour Picker */}
      <div
        className='relative w-16 h-[120px] overflow-hidden'
        style={{
          maskImage:
            'linear-gradient(to top, transparent, transparent 5%, white 20%, white 80%, transparent 95%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to top, transparent, transparent 5%, white 20%, white 80%, transparent 95%, transparent)',
        }}
      >
        <div
          ref={hourRef}
          className='h-full overflow-y-scroll snap-y snap-mandatory'
          style={{
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
          onScroll={() => handleScroll(hourRef, setHour, hourOptions)}
        >
          <div className='h-[42px]' />
          <div className='h-[42px]' />
          {hourOptions.map((h) => (
            <div
              key={h}
              className={`text-center text-gray p-1 h-[42px] flex items-center justify-center snap-center ${
                hours === h
                  ? '#110F09 font-bold text-[32px]'
                  : 'text-[24px] opacity-40'
              }`}
            >
              {h}
            </div>
          ))}
          <div className='h-[42px]' />
          <div className='h-[42px]' />
        </div>
      </div>

      <span
        className='text-2xl mx-2 font-bold text-[32px]'
        style={{ fontFamily: 'monospace' }}
      >
        :
      </span>

      {/* Minute Picker */}
      <div
        className='relative w-16 h-[120px] overflow-hidden'
        style={{
          maskImage:
            'linear-gradient(to top, transparent, transparent 5%, white 20%, white 80%, transparent 95%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to top, transparent, transparent 5%, white 20%, white 80%, transparent 95%, transparent)',
        }}
      >
        <div
          ref={minuteRef}
          className='h-full overflow-y-scroll snap-y snap-mandatory'
          style={{
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
          onScroll={() => handleScroll(minuteRef, setMinute, minuteOptions)}
        >
          <div className='h-[42px]' />
          <div className='h-[42px]' />
          {minuteOptions.map((m) => (
            <div
              key={m}
              className={`text-center text-gray p-1 h-[42px] flex items-center justify-center snap-center ${
                minutes === m
                  ? 'text-[#110F09] font-bold text-[32px]'
                  : 'text-[24px] opacity-40'
              }`}
            >
              {m}
            </div>
          ))}
          <div className='h-[42px]' />
          <div className='h-[42px]' />
        </div>
      </div>

      {/* Period Picker */}
      <div
        className='relative w-16 h-[120px] overflow-hidden'
        style={{
          maskImage:
            'linear-gradient(to top, transparent, transparent 5%, white 20%, white 80%, transparent 95%, transparent)',
          WebkitMaskImage:
            'linear-gradient(to top, transparent, transparent 5%, white 20%, white 80%, transparent 95%, transparent)',
        }}
      >
        <div
          ref={periodRef}
          className='h-full overflow-y-scroll snap-y snap-mandatory'
          style={{
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
          }}
          onScroll={() => handleScroll(periodRef, setPeriod, periodOptions)}
        >
          <div className='h-[42px]' />
          <div className='h-[42px]' />
          {periodOptions.map((p) => (
            <div
              key={p}
              className={`text-center text-gray p-1 h-[42px] flex items-center justify-center snap-center ${
                period === p
                  ? 'text-[#110F09] font-bold text-[32px]'
                  : 'text-[24px] opacity-40'
              }`}
            >
              {p}
            </div>
          ))}
          <div className='h-[42px]' />
          <div className='h-[42px]' />
        </div>
      </div>
    </div>
  );
};

export default TimeSelector;
